import React, { type ReactElement } from "react"

import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  type XAxisTickContentProps,
  YAxis,
} from "recharts"

import { Box, Typography, useTheme } from "@mui/material"
import type { Theme } from "@mui/material/styles"

import { LEFT_AREA_CHART_ADDITIONAL_SPACING } from "../stacked-area-graph/stacked-area-graph.types"
import { StackedBarGraphLegend } from "./stacked-bar-graph-legend"
import type {
  StackedBarGraphProps,
  StackedBarGraphSeries,
} from "./stacked-bar-graph.types"

const AXIS_TICK_FONT_SIZE = 12
const DEFAULT_CHART_EDGE_MARGIN = 5
const MAX_BAR_SIZE = 128
const RIGHT_CHART_TICK_MARGIN = 60
const X_AXIS_HEIGHT = 45
const X_AXIS_SPLIT_TICK_HEIGHT = 60

const stackedBarColors = (theme: Theme): string[] => [
  theme.palette.spectrum.cerulean[400],
  theme.palette.spectrum.cerulean[300],
  theme.palette.spectrum.cerulean[200],
  theme.palette.spectrum.cerulean[100],
  theme.palette.spectrum.cerulean[50],
]

const isPlottedSeries = (
  item: StackedBarGraphSeries,
  selectedSeriesKey: string | null | undefined
): boolean => selectedSeriesKey == null || item.key === selectedSeriesKey

const toAxisOffset = (value: number | string | undefined): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

interface StackedBarAxisTickProps {
  className?: string
  dx?: number | string
  dy?: number | string
  fill?: string
  label: string
  showDateLine: boolean
  textAnchor?: XAxisTickContentProps["textAnchor"]
  x?: number | string
  y?: number | string
}

interface SplitAxisTickProps extends XAxisTickContentProps {
  dx?: number | string
  dy?: number | string
}

const StackedBarAxisTick: React.FC<StackedBarAxisTickProps> = ({
  className,
  dx,
  dy,
  fill,
  label,
  showDateLine,
  textAnchor = "middle",
  x,
  y,
}) => {
  const lines = label.split("\n")
  const originX = toAxisOffset(x) + toAxisOffset(dx)
  const originY = toAxisOffset(y) + toAxisOffset(dy)

  return (
    <text
      className={className}
      fill={fill}
      fontSize={AXIS_TICK_FONT_SIZE}
      textAnchor={textAnchor}
      x={originX}
      y={originY}
    >
      <tspan dy="0.71em" x={originX}>
        {lines[0]}
      </tspan>
      {showDateLine ? (
        <tspan dy="1em" x={originX}>
          {lines[1]}
        </tspan>
      ) : null}
    </text>
  )
}

const isAxisTickValue = (value: unknown): value is number | string =>
  typeof value === "number" || typeof value === "string"

const isAxisDateLine = (dateLine: string | undefined): dateLine is string =>
  dateLine != null && dateLine !== ""

const formatSplitAxisTickLabel = (
  rawValue: number | string,
  index: number,
  tickFormatter: SplitAxisTickProps["tickFormatter"]
): string =>
  tickFormatter == null
    ? String(rawValue)
    : tickFormatter(String(rawValue), index)

// Recharts stores `tick` in an Immer store and deep-freezes it. An element
// would put this list on props, and the production bundle lets that freeze
// reach the list. A render function keeps the list in a closure. Index the
// list so a second pass over the same ticks stays idempotent.
const renderSplitAxisTick = () => {
  const visibleTickDates: (string | undefined)[] = []

  return (tickProps: SplitAxisTickProps): ReactElement | null => {
    if (!isAxisTickValue(tickProps.payload.value)) {
      return null
    }
    const rawValue = tickProps.payload.value

    const { index } = tickProps
    const label = formatSplitAxisTickLabel(
      rawValue,
      index,
      tickProps.tickFormatter
    )
    const dateLine = label.split("\n")[1]
    const showDateLine =
      isAxisDateLine(dateLine) && dateLine !== visibleTickDates[index - 1]
    if (isAxisDateLine(dateLine)) {
      visibleTickDates[index] = dateLine
    }

    return (
      <StackedBarAxisTick
        className={tickProps.className}
        dx={tickProps.dx}
        dy={tickProps.dy}
        fill={tickProps.fill}
        label={label}
        showDateLine={showDateLine}
        textAnchor={tickProps.textAnchor}
        x={tickProps.x}
        y={tickProps.y}
      />
    )
  }
}

export const StackedBarGraph: React.FC<StackedBarGraphProps> = ({
  barCategoryGap,
  customTooltip,
  data,
  formatBucketLabel,
  onSelectedSeriesKeyChange,
  selectedSeriesKey,
  series,
  showVerticalGrid = true,
  splitAxisTickLines = false,
  tickFormatter,
  title,
  yAxisLabel,
}) => {
  const theme = useTheme()
  const colors = stackedBarColors(theme)
  const hasData = data.length > 0 && series.length > 0
  const coloredSeries = series.map((item, index) => ({
    ...item,
    color: colors[index % colors.length],
  }))
  const plottedSeries = coloredSeries.filter((item) =>
    isPlottedSeries(item, selectedSeriesKey)
  )

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {hasData ? (
        <>
          {title ? (
            <Typography component="h2" sx={{ pb: 1 }} variant="subtitle1">
              {title}
            </Typography>
          ) : null}
          <StackedBarGraphLegend
            items={coloredSeries}
            onSelectedSeriesKeyChange={onSelectedSeriesKeyChange}
            selectedSeriesKey={selectedSeriesKey}
          />
        </>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <ResponsiveContainer width="100%">
          <BarChart
            barCategoryGap={barCategoryGap}
            data={data}
            margin={{
              bottom: DEFAULT_CHART_EDGE_MARGIN,
              left: LEFT_AREA_CHART_ADDITIONAL_SPACING,
              right: RIGHT_CHART_TICK_MARGIN,
              top: DEFAULT_CHART_EDGE_MARGIN,
            }}
            maxBarSize={MAX_BAR_SIZE}
            stackOffset="sign"
          >
            <CartesianGrid
              stroke={theme.palette.grey[100]}
              strokeDasharray="0"
              vertical={showVerticalGrid}
            />
            <XAxis
              dataKey="xaxis"
              dx={7}
              dy={5}
              height={
                splitAxisTickLines ? X_AXIS_SPLIT_TICK_HEIGHT : X_AXIS_HEIGHT
              }
              interval="preserveStartEnd"
              padding={{ left: 0.5 }}
              stroke={theme.palette.grey[700]}
              style={{ fontSize: "12px" }}
              tick={splitAxisTickLines ? renderSplitAxisTick() : true}
              tickFormatter={formatBucketLabel}
              tickLine={false}
            />
            <YAxis
              dx={0}
              label={
                <Label
                  angle={-90}
                  fill={theme.palette.grey[700]}
                  fontSize="12px"
                  position="left"
                  style={{ textAnchor: "middle" }}
                  value={yAxisLabel}
                />
              }
              padding={{ bottom: 0.5 }}
              stroke={theme.palette.grey[300]}
              tick={{ fill: theme.palette.grey[600], fontSize: 12 }}
              tickFormatter={tickFormatter}
              tickLine={false}
              width={44}
            />
            {plottedSeries.map((item) => (
              <Bar
                key={item.key}
                dataKey={item.key}
                fill={item.color}
                isAnimationActive={false}
                name={item.label}
                // Recharts drops 0-height bars, then tooltip hover uses the remaining index.
                // A custom shape keeps those categories so the hovered bar maps to the right point.
                shape={<Rectangle />}
                stackId="stack"
              />
            ))}
            {customTooltip ? (
              <Tooltip
                content={customTooltip}
                cursor={{ fill: theme.palette.grey[100] }}
                isAnimationActive={false}
                shared={false}
              />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}
